import { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X, User, Building2, Check } from 'lucide-react'
import { useOrgMembers } from '../../hooks/useOrganization'
import { useRecentParties } from '../../hooks/usePartySearch'
import type { OrgMember } from '../../services/organizationApi'
import type { PartySearchResult } from '../../services/partiesSearchApi'

interface PickerItem {
  id: string
  name: string
  email: string
  subtitle?: string
  icon: 'person' | 'org'
}

interface MemberPickerProps {
  actorId?: string
  orgKoblinkId?: string
  /** Emails already added as signers — shown as disabled/checked */
  existingEmails: string[]
  onSelect: (name: string, email: string) => void
  onClose: () => void
}

function buildPickerItems(
  parties?: PartySearchResult[],
  orgMembers?: OrgMember[],
): PickerItem[] {
  const items: PickerItem[] = []
  const seenEmails = new Set<string>()

  // Org members first (they have role info)
  if (orgMembers) {
    for (const m of orgMembers) {
      if (m.email) {
        const emailLower = m.email.toLowerCase()
        if (!seenEmails.has(emailLower)) {
          seenEmails.add(emailLower)
          items.push({
            id: `member-${m.id}`,
            name: m.display_name,
            email: m.email,
            subtitle: m.role,
            icon: 'org',
          })
        }
      }
    }
  }

  // Then parties (actors with relationships)
  if (parties) {
    for (const p of parties) {
      if (p.email) {
        const emailLower = p.email.toLowerCase()
        if (!seenEmails.has(emailLower)) {
          seenEmails.add(emailLower)
          items.push({
            id: `party-${p.koblink_id}`,
            name: p.display_name,
            email: p.email,
            icon: p.actor_type === 'ORGANIZATION' ? 'org' : 'person',
          })
        }
      }
    }
  }

  return items
}

export function MemberPicker({ actorId, orgKoblinkId, existingEmails, onSelect, onClose }: MemberPickerProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const isOrg = !!orgKoblinkId

  // Fetch parties via PartyPreference recent endpoint
  const { data: recentParties, isLoading: partiesLoading } = useRecentParties(actorId, 100)

  // Also fetch org members if on org account
  const { data: orgMembers, isLoading: membersLoading } = useOrgMembers(orgKoblinkId)

  const isLoading = isOrg ? (partiesLoading && membersLoading) : partiesLoading

  const items = useMemo(
    () => buildPickerItems(recentParties, isOrg ? orgMembers : undefined),
    [recentParties, isOrg, orgMembers],
  )

  const filtered = useMemo(() => {
    if (!search.trim()) return items
    const q = search.toLowerCase()
    return items.filter(
      (i) => i.name.toLowerCase().includes(q) || i.email.toLowerCase().includes(q),
    )
  }, [items, search])

  const existingSet = useMemo(
    () => new Set(existingEmails.map((e) => e.toLowerCase())),
    [existingEmails],
  )

  // Focus search on mount
  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const title = isOrg ? t('contacts.browseOrgMembers') : t('contacts.browseParties')

  return (
    <div
      ref={panelRef}
      className="absolute left-0 right-0 top-0 z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg flex flex-col"
      style={{ maxHeight: '320px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 dark:border-neutral-700">
        <span className="text-xs font-medium text-neutral-900 dark:text-neutral-100">
          {title}
        </span>
        <button
          onClick={onClose}
          className="p-0.5 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-neutral-100 dark:border-neutral-700">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            ref={searchRef}
            type="text"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-1">
        {isLoading ? (
          <p className="text-xs text-neutral-400 text-center py-4">{t('common.loading')}</p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-neutral-400 text-center py-4">{t('common.noResults')}</p>
        ) : (
          filtered.map((item) => {
            const alreadyAdded = existingSet.has(item.email.toLowerCase())
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (!alreadyAdded) {
                    onSelect(item.name, item.email)
                  }
                }}
                disabled={alreadyAdded}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                  alreadyAdded
                    ? 'opacity-50 cursor-default'
                    : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                }`}
              >
                {item.icon === 'org' ? (
                  <Building2 size={12} className="shrink-0 text-neutral-400" />
                ) : (
                  <User size={12} className="shrink-0 text-neutral-400" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100 truncate">
                    {item.name}
                  </p>
                  <p className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                    {item.email}
                    {item.subtitle && ` · ${item.subtitle}`}
                  </p>
                </div>
                {alreadyAdded && (
                  <Check size={12} className="shrink-0 text-primary-500" />
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
